<template>
  <div v-if="!isShopFloorWorkerUI()" class="top-header-line">
    <span class="brand-mark" data-tour-id="header.logo">
      <img
        src="@/assets/img/logo-small.png"
        alt="MRP One"
        class="brand-logo"
      />
      <span class="brand-text">MRP One</span>
      <!-- Free-tier indicator takes precedence over the DEMO badge. Self-signup
           admin only (showBillingUi). Lighter than the demo badge; clicking it
           opens the upgrade dialog. -->
      <span
        v-if="showFreeTierIndicator"
        class="free-tier-badge clickable"
        :title="translate('You\'re on the Always Free plan. Click to upgrade.')"
        @click="onFreeTierClick">{{ translate('Always Free') }}</span>
      <span
        v-else-if="isDemoTenant"
        class="demo-badge clickable"
        :title="translate('Click to switch to production mode and remove demo data')"
        @click="onDemoBadgeClick">DEMO</span>

      <span
        v-if="showFreeTierIndicator && nearCapLimit"
        class="near-cap-note"
        :title="nearCapNote">{{ nearCapNote }}</span>
    </span>

    <div class="top-header-line-info" v-if="isLogged">
      
      <img
        :src="getCompanyLogoSmall()"
        style="margin-right: 20px; height:16px; width:16px"
      /><span style="margin-right: 20px">{{ currentUser.company }}</span>

      <select v-if="hasSites()" :title="translate('Site')" v-model="persistedSelectedSite" >
        <!--:onchange="onSiteChanged()"-->
        <option v-for="site in sites()" :key="site.id" :value="site.id">[ {{ site.name }} ]</option>
      </select> 

      <span>{{ currentUser.userName }}</span>
      <span style="margin-right: 40px"></span>

      <router-link to="/alerts" v-if="isLogged">
        <el-badge :value="getNotificationsCount()"  >
          <el-icon size="large" color="var(--main-menu-alt-text-color)"><Message/></el-icon>
        </el-badge>
      </router-link>

      <span style="margin-right: 40px"></span>

      <cc-help-menu />

    </div>
    <!-- el-menu and its children comes from Element UI -->
    <!-- http://element.eleme.io/#/en-US/component/menu -->
    <!--THIS DOESN'T WORK: background-color="var(--main-menu-background-color)" -->
    <el-menu
      ref="mainMenu"
      mode="horizontal"
      background-color="#545c64"
      text-color="var(--main-menu-text-color)"
      active-text-color="var(--main-menu-active-text-color)"
      @select="navigate"
    >
      <template v-for="(menuItem, idx) in getMainMenuItems" >
        <div class="flex-grow" :key="idx" v-if="menuItem.id=='settings'" />
        <el-menu-item
          v-if="!menuItem.subMenuItems || menuItem.subMenuItems.length === 0"
          :index="menuItem.id"
          :key="idx"
          selected
          :class="menuItem.class"
          :data-tour-id="tourIdForMenu(menuItem.id)"
        >
          <template v-if="menuItem.icon">
              <el-icon><component :is="menuItem.icon" /></el-icon>
            </template>
          {{ translate(menuItem.text) }}
        </el-menu-item>
        <el-sub-menu v-else :index="menuItem.id" :key="idx+100">
          <template #title>
            <template v-if="menuItem.icon">
              <el-badge
                v-if="menuItem.id === 'settings' && hasAccountWarnings"
                is-dot
                type="warning"
                class="settings-warning-badge">
                <el-icon><component :is="menuItem.icon" /></el-icon>
              </el-badge>
              <el-icon v-else><component :is="menuItem.icon" /></el-icon>
            </template>
            {{ translate(menuItem.text) }}
          </template>
          <el-menu-item v-for="(subItem, subIdx) in menuItem.subMenuItems" :key="subIdx" :index="subItem.id">
            <template v-if="subItem.icon">
              <el-icon><component :is="subItem.icon" /></el-icon>
            </template>
            {{ translate(subItem.text) }}
            <span
              v-if="subItem.id === 'settings.planbilling' && hasAccountWarnings"
              class="submenu-warning-mark"
              :title="translate('Please verify your email address')">!</span>
          </el-menu-item>
        </el-sub-menu>
      </template>
    </el-menu>

  </div>
</template>

<script>
/* eslint-disable */
import { markRaw } from 'vue'
import { Setting, Message } from '@element-plus/icons-vue'

import { mapActions, mapGetters } from 'vuex'
import { version } from '../../config'
import { setSite } from '../../plugins/http'
import tierGuard from '../../mixins/tierGuard'

export default {
  components: {
    Setting: markRaw(Setting),
    Message: markRaw(Message),
  },
  name: 'CcNavBar',
  mixins: [tierGuard],
  data: function () {
    return {
      mobileAppLinksDialogVisible: false,
      selectedSite: '0',
    }
  },
  computed: {
    /**
     * See src/app/auth/vuex/getters.js
     */
    ...mapGetters([
      'currentUser',
      'isLogged',
      'isShopFloorWorker',
      //'mainMenuItems',
    ]),

    version() {
      return version
    },

    isDemoTenant() {
      return !!(this.currentUser && this.currentUser.tenantUsedDemoData)
    },

    // Always-Free indicator: free tier AND the self-signup tenant admin
    // (showBillingUi). Other users never see the upgrade-bearing indicator.
    showFreeTierIndicator() {
      return this.isFreeTier && this.showBillingUi
    },

    // First metered resource at >= 80% of its limit (NaN-safe), or null.
    // Checks users / workcenters / workers / documents / active orders.
    nearCapLimit() {
      const limits = this.tierLimits
      const usage = this.tierUsage
      if (!limits || !usage) return null
      const kinds = ['users', 'workcenters', 'workers', 'documents', 'activeOrders']
      for (let i = 0; i < kinds.length; i++) {
        const k = kinds[i]
        const max = limits[k]
        const used = usage[k]
        if (max > 0 && used >= 0.8 * max) {
          return { kind: k, current: used, max }
        }
      }
      return null
    },

    nearCapNote() {
      const nc = this.nearCapLimit
      if (!nc) return ''
      return this.translateFormat(
        "You're close to your {0} limit ({1}/{2})",
        [this.translate(this.limitLabel(nc.kind)), nc.current, nc.max].join(',')
      )
    },

    persistedSelectedSite: {
      get() {
        var res = localStorage.getItem('selectedSite') || this.selectedSite;
        var sites = this.sites();
        if(sites) {
          var found = sites.find(n => n.id == res);
          if(!found) {
            res = sites[0].id;
            setSite(res);
          }
        }
        return res;
      },
      set(value) {
        this.selectedSite = value;
        localStorage.setItem('selectedSite', value);
        setSite(this.selectedSite);

        this.$emit('site-changed', value);
      },
    },

  },
  watch: {
    isLogged(value) {
      // isLogged changes when the token changes
      if (value === false) {
        this.$router.push({ name: 'auth.signin' })
      } else {
      }
    },
  },
  methods: {
    /**
     * Makes logout() action available withint this component
     * See /src/app/auth/vuex/actions.js
     */

    ...mapActions(['logout']),

    /* eslint-disable no-undef */
    navigate(name) {
      switch (name) {
        case 'prodexis':
          window.location = 'https://mrpone.com'
          break
        case 'logout':
          this.logout()
          break
        default:
          this.$router.push({ name }, () => {})
          break
      }
    },

    hasSites() {
      return this.currentUser.hasSites;
    },

    sites() {
      return this.currentUser.sites.length == 0 
        ? [{id: -1, name: '-'}]
        : this.currentUser.sites.length == 1
          ? this.currentUser.sites
          : [{id: 0, name: this.translate('all')}, ...this.currentUser.sites];
    },
    
    isShopFloorWorkerUI() {
      return this.isShopFloorWorker
    },

    getCompanyLogoSmall() {
      return this.currentUser.companyLogoSmall &&
        this.currentUser.companyLogoSmall.data
        ? 'data:image/png;base64, ' + this.currentUser.companyLogoSmall.data
        : ''
    },

    getNotificationsCount() {
      return this.currentUser.notificationsCount > 0
        ? this.currentUser.notificationsCount
        : null
    },

    /**
     * Click on the DEMO badge → confirm switch to production mode → delete demo business data
     * via POST /v1/wizard/exit-demo, then refresh the user (so isDemoTenant flips off) and
     * reload the current route so the page re-fetches and the now-empty data renders cleanly.
     */
    // Always-Free indicator click → open the upgrade dialog (tierGuard mixin).
    // Pre-fill with the near-cap resource if there is one, else a generic prompt.
    onFreeTierClick() {
      const nc = this.nearCapLimit
      if (nc) {
        this.openUpgradeDialog(nc.kind, { limit: nc.kind, current: nc.current, max: nc.max })
      } else {
        this.openUpgradeDialog('plan')
      }
    },

    onDemoBadgeClick() {
      this.$swal({
        icon: 'warning',
        title: this.translate('Switch to production mode?'),
        html: this.translate('This will delete all demo data (inquiries, orders, components, customers, providers, material orders, external cooperations and their derivatives). Configured resources (workcenter groups, workcenters, workers, users, roles, sites) will be kept.'),
        showCancelButton: true,
        confirmButtonText: this.translate('Yes, switch to production'),
        cancelButtonText: this.translate('Cancel'),
        confirmButtonColor: '#dc2626',
      }).then((res) => {
        if (!res || !res.isConfirmed) return
        this.$http
          .post('v1/wizard/exit-demo')
          .then(() => {
            this.$store.dispatch('loadUser').then(() => {
              // Hard reload so every cached list view fetches fresh (now empty) data and the
              // DEMO badge is gone — current URL is preserved.
              window.location.reload()
            })
          })
          .catch((error) => {
            this.showApiError(error)
          })
      })
    },

    // Map a menu item id -> data-tour-id used as the el-tour-step target selector.
    // Returns null for menu items not referenced by any tour so the attribute is omitted.
    tourIdForMenu(menuItemId) {
      switch (menuItemId) {
        case 'inquiries.index': return 'menu.inquiries'
        case 'orders.index': return 'menu.orders'
        case 'components.index': return 'menu.products'
        case 'workcenters.index': return 'menu.workcenters'
        case 'schedule.index': return 'menu.schedule'
        case 'ordercontrol.index': return 'menu.ordercontrol'
        default: return null
      }
    },
  },

  mounted() {
    // const baseUrl = process.env.BASE_URL
    this.selectedSite = localStorage.getItem('selectedSite') || null;
    setSite(this.selectedSite);
  },
  
  updated() {},
}
</script>

<style scoped>

.flex-grow {
  flex-grow: 0.6;
}

.brand-mark {
  float: left;
  margin-left: 20px;
  padding-top: 4px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  line-height: 1;
}

.brand-logo {
  max-width: 100px;
  max-height: 20px;
  display: inline-block;
}

.brand-text {
  /* Brighter blue than --active-tab-color (rgb(60,101,162)), which is too dark
     to read on the dark navbar. Local override only — the variable stays as-is
     for the light-background usages elsewhere. */
  color: #60a5fa;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.4px;
  white-space: nowrap;
}

.demo-badge {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  background-color: #f59e0b;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  border-radius: 4px;
  line-height: 1.4;
  white-space: nowrap;
  text-transform: uppercase;
}
.demo-badge.clickable {
  cursor: pointer;
  user-select: none;
}
.demo-badge.clickable:hover {
  background-color: #d97706;
}

/* Free-tier indicator: steady-state, lighter than the (amber) demo badge —
   a subtle outlined pill rather than a filled attention badge. */
.free-tier-badge {
  display: inline-block;
  margin-left: 8px;
  padding: 1px 8px;
  background-color: transparent;
  /* Brighter blue for contrast on the dark navbar (see .brand-text). */
  color: #60a5fa;
  border: 1px solid #60a5fa;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.4px;
  border-radius: 10px;
  line-height: 1.5;
  white-space: nowrap;
}
.free-tier-badge.clickable {
  cursor: pointer;
  user-select: none;
}
.free-tier-badge.clickable:hover {
  opacity: 1;
}

/* Amber near-cap note shown next to the Always-Free indicator. */
.near-cap-note {
  display: inline-block;
  margin-left: 8px;
  color: var(--el-color-warning, #e6a23c);
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.menu-item-right {
  right: 8px;
   position: absolute;
}

select {
  border: 0px;
  margin-right: 20px;
  color:var(--main-menu-text-color);
  background-color: var(--main-menu-background-color);
}

.settings-warning-badge :deep(.el-badge__content) {
  background-color: var(--el-color-warning, #e6a23c);
  border: none;
  top: 12px;
}

.submenu-warning-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: var(--el-color-warning, #e6a23c);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
}
.el-menu--horizontal.el-menu {
  height: 50px;
}
</style>
